import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { DashboardLayout } from '../../components/DashboardLayout';
import { getTrafficUserActivities } from '../../services/adminApi';
import { useAdminTrafficLive } from '../../hooks/useAdminTrafficLive';
import { useAuthStore } from '../../store/authStore';
import {
  AdminTrafficActivitiesResponse,
  AdminTrafficActivity
} from '../../types';
import glassTheme, { glassMixins } from '../../styles/glassTheme';

const ROLE_LABELS: Record<string, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  moderator: '内容审核员',
  analyst: '数据分析员',
  content_manager: '内容管理员',
  teacher: '教师',
  student: '学生'
};
const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: ${glassTheme.spacing.lg};
  color: ${glassTheme.colors.accent.blue};
  text-decoration: underline;
  text-underline-offset: 0.2em;
  &:focus-visible { outline: 3px solid rgba(96, 165, 250, 0.3); outline-offset: 2px; }
`;
const Container = styled.section`
  ${glassTheme.glass.container}
  padding: ${glassTheme.spacing.lg};
  margin-bottom: ${glassTheme.spacing.lg};
  overflow-x: auto;
`;
const Notice = styled.div<{ $error?: boolean }>`
  ${glassTheme.glass.light}
  padding: ${glassTheme.spacing.md};
  margin-bottom: ${glassTheme.spacing.lg};
  color: ${props => props.$error ? glassTheme.colors.status.error : glassTheme.colors.text.secondary};
`;
const Summary = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${glassTheme.spacing.md} ${glassTheme.spacing.xl};
  margin: 0;
  div { min-width: 0; }
  dt { color: ${glassTheme.colors.text.muted}; margin-bottom: ${glassTheme.spacing.xs}; }
  dd { margin: 0; color: ${glassTheme.colors.text.primary}; overflow-wrap: anywhere; }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;
const SectionHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${glassTheme.spacing.sm};
`;
const LiveStatus = styled.span<{ $state: 'live' | 'reconnecting' | 'stale' }>`
  color: ${props => props.$state === 'live'
    ? glassTheme.colors.status.success
    : props.$state === 'stale'
      ? glassTheme.colors.status.error
      : glassTheme.colors.status.warning};
  font-weight: 600;
`;
const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
  @media (max-width: 768px) {
    display: none;
  }
`;
const MobileActivityList = styled.ol`
  display: none;
  margin: 0;
  padding: 0;
  list-style-position: inside;
  @media (max-width: 768px) {
    display: grid;
    gap: ${glassTheme.spacing.md};
  }
`;
const MobileActivityCard = styled.li`
  ${glassTheme.glass.light}
  padding: ${glassTheme.spacing.md};
  color: ${glassTheme.colors.text.primary};
  div { margin-top: ${glassTheme.spacing.xs}; }
  strong { color: ${glassTheme.colors.text.muted}; }
`;
const Th = styled.th`
  padding: ${glassTheme.spacing.md};
  text-align: left;
  color: ${glassTheme.colors.text.secondary};
  border-bottom: 1px solid ${glassTheme.colors.border};
`;
const Td = styled.td`
  padding: ${glassTheme.spacing.md};
  color: ${glassTheme.colors.text.primary};
  border-bottom: 1px solid ${glassTheme.colors.border};
  vertical-align: top;
`;
const Button = styled.button`
  min-height: 44px;
  padding: ${glassTheme.spacing.sm} ${glassTheme.spacing.lg};
  background: transparent;
  color: ${glassTheme.colors.text.primary};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.sm};
  cursor: pointer;
  &:focus-visible { outline: 3px solid rgba(96, 165, 250, 0.3); outline-offset: 2px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const Filters = styled.div`
  ${glassTheme.glass.light}
  display: flex;
  gap: ${glassTheme.spacing.lg};
  align-items: end;
  flex-wrap: wrap;
  padding: ${glassTheme.spacing.md};
  margin-bottom: ${glassTheme.spacing.lg};
  > div { min-width: 220px; flex: 1; }
  label { display: block; margin-bottom: ${glassTheme.spacing.xs}; color: ${glassTheme.colors.text.secondary}; }
  select {
    ${glassMixins.input}
    width: 100%;
    min-height: 44px;
  }
  @media (max-width: 768px) { > div { flex-basis: 100%; } }
`;
const Outcome = styled.span<{ $success: boolean }>`
  display: inline-block;
  padding: ${glassTheme.spacing.xs} ${glassTheme.spacing.sm};
  border-radius: ${glassTheme.borderRadius.full};
  color: ${props => props.$success
    ? glassTheme.colors.status.success
    : glassTheme.colors.status.error};
`;

const utcDate = (value: string) => new Date(value).toISOString().slice(0, 10);
const utcTimestamp = (value: string) =>
  `${new Date(value).toISOString().replace('T', ' ').slice(0, 19)} UTC`;
const compareActivities = (
  left: AdminTrafficActivity,
  right: AdminTrafficActivity
) => right.recordedAt.localeCompare(left.recordedAt) || right.id.localeCompare(left.id);
const mergeActivities = (
  current: AdminTrafficActivity[],
  fresh: AdminTrafficActivity[]
) => Array.from(
  new Map([...current, ...fresh].map(activity => [activity.id, activity])).values()
).sort(compareActivities);
const reconcileRefreshedActivities = (
  current: AdminTrafficActivity[],
  fresh: AdminTrafficActivity[],
  freshBoundary: AdminTrafficActivity | null,
  nextCursor: string | null,
  window: AdminTrafficActivitiesResponse['window']
) => {
  // A first-page refresh is authoritative through its last row. Keep only
  // previously paginated rows below that boundary and still inside the latest
  // server window; refreshed copies must win so IP expiry/changes are immediate.
  const retained = nextCursor && freshBoundary
    ? current.filter(activity =>
        activity.recordedAt >= window.start
        && activity.recordedAt <= window.end
        && compareActivities(activity, freshBoundary) > 0
      )
    : [];
  return mergeActivities(retained, fresh);
};

const AdminTrafficUserActivityContent: React.FC<{ userId: string }> = ({ userId }) => {
  const navigate = useNavigate();
  const logout = useAuthStore(state => state.logout);
  const [data, setData] = useState<AdminTrafficActivitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<'load' | 'filter' | 'more' | 'not-found' | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [pendingCategory, setPendingCategory] = useState('');
  const [pendingType, setPendingType] = useState('');
  const firstController = useRef<AbortController | null>(null);
  const moreController = useRef<AbortController | null>(null);
  const loadedMore = useRef(false);

  const handleAuthError = useCallback((requestError: any): boolean => {
    const status = requestError?.response?.status;
    if (status === 401) {
      toast.error('登录已过期，请重新登录');
      logout(false);
      navigate('/');
      return true;
    }
    if (status === 403) {
      toast.error('没有权限查看用户活动');
      navigate('/learn');
      return true;
    }
    return false;
  }, [logout, navigate]);

  const loadFirst = useCallback(async (
    background = false,
    requestedCategory = category,
    requestedType = type,
    commitFilters = false
  ): Promise<boolean> => {
    firstController.current?.abort();
    moreController.current?.abort();
    moreController.current = null;
    const controller = new AbortController();
    firstController.current = controller;
    setLoadingMore(false);
    if (!background) setLoading(true);
    setError(null);
    try {
      const response = requestedCategory || requestedType || background
        ? await getTrafficUserActivities(
            userId, 50, undefined, controller.signal,
            requestedCategory || undefined, requestedType || undefined, background
          )
        : await getTrafficUserActivities(userId, 50, undefined, controller.signal);
      if (controller.signal.aborted || firstController.current !== controller) return false;
      const knownTypes = new Set(
        response.catalog?.types.map(item => item.type)
        || response.activities.map(item => item.type)
      );
      const safeResponse = {
        ...response,
        activities: response.activities.filter(activity => knownTypes.has(activity.type))
      };
      const hadLoadedMore = loadedMore.current;
      const freshBoundary = response.activities[response.activities.length - 1] || null;
      setData(previous => previous && hadLoadedMore && !commitFilters
        ? {
            ...safeResponse,
            activities: reconcileRefreshedActivities(
              previous.activities,
              safeResponse.activities,
              freshBoundary,
              safeResponse.nextCursor,
              safeResponse.window
            )
          }
        : safeResponse);
      if (commitFilters) {
        setCategory(requestedCategory);
        setType(requestedType);
        setPendingCategory(requestedCategory);
        setPendingType(requestedType);
        loadedMore.current = false;
      } else if (safeResponse.nextCursor === null) {
        loadedMore.current = false;
      }
      setAnnouncement(background ? '用户活动已更新' : '用户活动已加载');
      return true;
    } catch (requestError: any) {
      if (controller.signal.aborted || requestError?.code === 'ERR_CANCELED') return false;
      if (handleAuthError(requestError)) return false;
      if (requestError?.response?.status === 404) {
        setData(null);
        setCategory('');
        setType('');
        setPendingCategory('');
        setPendingType('');
        loadedMore.current = false;
        setAnnouncement('未找到该用户');
        setError('not-found');
      } else if (commitFilters) {
        setPendingCategory(category);
        setPendingType(type);
        setAnnouncement('活动筛选应用失败，仍显示上次确认的结果');
        setError('filter');
      } else {
        setError('load');
      }
      return false;
    } finally {
      if (!background && !controller.signal.aborted && firstController.current === controller) {
        setLoading(false);
      }
    }
  }, [category, handleAuthError, type, userId]);

  useEffect(() => {
    void loadFirst(false, '', '', false);
    return () => {
      firstController.current?.abort();
      moreController.current?.abort();
    };
    // User-scoped content is keyed by userId; filter changes are explicitly
    // loaded and committed only after their request succeeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  useEffect(() => {
    const heading = document.querySelector('h1');
    heading?.setAttribute('tabindex', '-1');
    (heading as HTMLElement | null)?.focus();
  }, []);

  const loadMore = useCallback(async () => {
    if (!data?.nextCursor || loadingMore) return;
    moreController.current?.abort();
    const controller = new AbortController();
    moreController.current = controller;
    setLoadingMore(true);
    setError(null);
    try {
      const response = category || type
        ? await getTrafficUserActivities(
            userId, 50, data.nextCursor, controller.signal,
            category || undefined, type || undefined
          )
        : await getTrafficUserActivities(userId, 50, data.nextCursor, controller.signal);
      if (controller.signal.aborted || moreController.current !== controller) return;
      const knownTypes = new Set(
        response.catalog?.types.map(item => item.type)
        || response.activities.map(item => item.type)
      );
      const safeActivities = response.activities.filter(activity => knownTypes.has(activity.type));
      loadedMore.current = true;
      setData(previous => previous && ({
        ...previous,
        activities: mergeActivities(previous.activities, safeActivities),
        nextCursor: response.nextCursor
      }));
      setAnnouncement('已加载更多活动');
    } catch (requestError: any) {
      if (controller.signal.aborted || requestError?.code === 'ERR_CANCELED') return;
      if (handleAuthError(requestError)) return;
      if (requestError?.response?.status === 404) {
        setData(null);
        setCategory('');
        setType('');
        setPendingCategory('');
        setPendingType('');
        loadedMore.current = false;
        setAnnouncement('未找到该用户');
        setError('not-found');
      } else {
        setError('more');
      }
    } finally {
      if (!controller.signal.aborted && moreController.current === controller) {
        moreController.current = null;
        setLoadingMore(false);
      }
    }
  }, [category, data?.nextCursor, handleAuthError, loadingMore, type, userId]);

  const { state: liveState, lastSyncedAt, markSynced } = useAdminTrafficLive({
    refresh: () => loadFirst(true),
    shouldRefresh: event =>
      event.affectedUserId === userId
      && (!category || data?.catalog?.types.find(item => item.type === event.type)?.category === category)
      && (!type || event.type === type),
    onAuthError: handleAuthError
  });
  const syncedGeneration = data?.generatedAt;
  useEffect(() => {
    if (syncedGeneration) markSynced();
  }, [syncedGeneration, markSynced]);
  const liveText = liveState === 'live'
    ? '实时更新'
    : liveState === 'stale'
      ? `数据可能已过期 · 上次同步 ${lastSyncedAt ? utcTimestamp(lastSyncedAt.toISOString()).slice(11) : '未成功'}`
      : '正在重连';

  return (
    <DashboardLayout
      title="用户活动"
      subtitle="查看近 30 天已安全记录的平台活动（UTC）"
      compact
    >
      <BackLink to="/admin/dashboard?tab=traffic">← 返回用户流量</BackLink>
      <span
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
      >
        {announcement}。{liveText}
      </span>

      {loading && !data && <Notice role="status" aria-busy="true">正在加载用户活动…</Notice>}
      {error === 'not-found' && (
        <Notice $error role="alert">未找到该用户，账户可能已删除。</Notice>
      )}
      {error === 'load' && !data && (
        <Notice $error role="alert">
          用户活动加载失败，请稍后重试。
          <div><Button onClick={() => void loadFirst()}>重试</Button></div>
        </Notice>
      )}

      {data && (
        <>
          <Container aria-label="用户账户摘要">
            <Summary>
              <div><dt>用户</dt><dd>{data.user.displayName || '未设置名称'}</dd></div>
              <div><dt>邮箱</dt><dd>{data.user.email}</dd></div>
              <div><dt>报告角色</dt><dd>{ROLE_LABELS[data.user.reportingRole] || data.user.reportingRole}</dd></div>
              <div><dt>统计范围</dt><dd>{utcDate(data.window.start)} 至 {utcDate(data.window.end)}（UTC）</dd></div>
            </Summary>
          </Container>

          <Notice>
            <strong>隐私与记录范围</strong>
            <p>仅限管理员用于平台运行与安全排查。活动从功能启用后开始记录，可能不完整，也不替代管理审计日志。IP 地址最多保留 30 天；会话与请求标识仅用于服务端关联，不在此页面显示。此处不会显示答案、分数、代码或输出、AI/聊天内容、解释或笔记、搜索文本、文件名、设备信息，也不提供导出或研究用途。</p>
          </Notice>

          <Filters aria-label="活动筛选">
            <div>
              <label htmlFor="activity-category">活动类别</label>
              <select
                id="activity-category"
                value={pendingCategory}
                disabled={loading || loadingMore}
                onChange={event => {
                  const nextCategory = event.target.value;
                  setPendingCategory(nextCategory);
                  setPendingType('');
                  setAnnouncement(nextCategory
                    ? `已选择${data.catalog?.categories.find(item => item.value === event.target.value)?.label || ''}，活动类型已重置`
                    : '已清除活动类别筛选');
                  void loadFirst(false, nextCategory, '', true);
                }}
              >
                <option value="">全部类别</option>
                {(data.catalog?.categories || []).map(item => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="activity-type">活动类型</label>
              <select
                id="activity-type"
                value={pendingType}
                disabled={!pendingCategory || loading || loadingMore}
                aria-describedby={!pendingCategory ? 'activity-type-help' : undefined}
                onChange={event => {
                  const nextType = event.target.value;
                  setPendingType(nextType);
                  void loadFirst(false, pendingCategory, nextType, true);
                }}
              >
                <option value="">全部活动</option>
                {(data.catalog?.types || [])
                  .filter(item => item.category === pendingCategory)
                  .map(item => <option key={item.type} value={item.type}>{item.label}</option>)}
              </select>
              {!pendingCategory && <small id="activity-type-help">请先选择一个类别</small>}
            </div>
            {(pendingCategory || pendingType) && (
              <Button onClick={() => {
                setPendingCategory('');
                setPendingType('');
                void loadFirst(false, '', '', true);
              }}>清除筛选</Button>
            )}
          </Filters>

          <SectionHeading>
            <h2>已记录活动</h2>
            <div>
              <LiveStatus $state={liveState}>{liveText}</LiveStatus>
              {liveState === 'stale' && (
                <Button onClick={() => void loadFirst(true)}>立即重试</Button>
              )}
            </div>
          </SectionHeading>
          {error === 'load' && (
            <Notice $error role="alert">
              用户活动加载失败，请稍后重试。
              <div><Button onClick={() => void loadFirst(true)}>重试</Button></div>
            </Notice>
          )}
          {error === 'filter' && (
            <Notice $error role="alert">
              活动筛选应用失败，仍显示上次确认的筛选结果，请稍后重试。
            </Notice>
          )}
          {loading && data && <Notice role="status" aria-busy="true">正在应用筛选…</Notice>}
          {data.activities.length === 0 ? (
            <Notice>
              {category || type ? '没有符合当前筛选条件的活动' : '近 30 天暂无已记录活动'}
              <p>活动从功能启用后开始记录；这不代表该用户没有进行其他操作。</p>
            </Notice>
          ) : (
            <Container
              tabIndex={0}
              aria-label="用户活动表（可横向滚动）"
              aria-busy={loadingMore}
            >
              <Table>
                <caption>该用户近 30 天已安全记录的平台活动</caption>
                <thead>
                  <tr><Th scope="col">记录时间</Th><Th scope="col">类别</Th><Th scope="col">活动</Th><Th scope="col">结果</Th><Th scope="col">目标</Th><Th scope="col">IP 地址（最多保留 30 天）</Th></tr>
                </thead>
                <tbody>
                  {data.activities.map(activity => (
                    <tr key={activity.id}>
                      <Td data-label="记录时间"><time dateTime={activity.recordedAt}>{utcTimestamp(activity.recordedAt)}</time></Td>
                      <Td data-label="类别">{activity.categoryLabel || '—'}</Td>
                      <Td data-label="活动">{activity.label}{activity.reasonLabel && <small><br />{activity.reasonLabel}</small>}</Td>
                      <Td data-label="结果">{activity.outcome
                        ? <Outcome $success={activity.outcome === 'success'}>{activity.outcome === 'success' ? '成功' : '失败'}</Outcome>
                        : '—'}</Td>
                      <Td data-label="目标">{activity.target || '—'}</Td>
                      <Td data-label="IP 地址">{activity.ipAddress || '未记录或已过期'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <MobileActivityList aria-label="用户活动卡片列表">
                {data.activities.map(activity => (
                  <MobileActivityCard key={activity.id}>
                    <div><strong>记录时间：</strong><time dateTime={activity.recordedAt}>{utcTimestamp(activity.recordedAt)}</time></div>
                    <div><strong>类别：</strong>{activity.categoryLabel || '—'}</div>
                    <div><strong>活动：</strong>{activity.label}{activity.reasonLabel && <small> · {activity.reasonLabel}</small>}</div>
                    <div><strong>结果：</strong>{activity.outcome
                      ? <Outcome $success={activity.outcome === 'success'}>{activity.outcome === 'success' ? '成功' : '失败'}</Outcome>
                      : '—'}</div>
                    <div><strong>目标：</strong>{activity.target || '—'}</div>
                    <div><strong>IP 地址：</strong>{activity.ipAddress || '未记录或已过期'}</div>
                  </MobileActivityCard>
                ))}
              </MobileActivityList>
            </Container>
          )}

          {error === 'more' && (
            <Notice $error role="alert">
              更多活动加载失败，请重试。
              <div><Button onClick={() => void loadMore()}>重试加载更多</Button></div>
            </Notice>
          )}
          {data.nextCursor ? (
            <Button disabled={loadingMore} onClick={() => void loadMore()}>
              {loadingMore ? '正在加载…' : '加载更多'}
            </Button>
          ) : (
            <p>已显示全部符合条件的已记录活动</p>
          )}
          <p>已显示 {data.activities.length} 条</p>
        </>
      )}
    </DashboardLayout>
  );
};

export const AdminTrafficUserActivityPage: React.FC = () => {
  const { userId = '' } = useParams();
  return <AdminTrafficUserActivityContent key={userId} userId={userId} />;
};
